import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Eye, EyeOff } from "lucide-react";
import { PanelSection } from "../PanelSection";
import { useSectionDisclosure } from "../useSectionDisclosure";
import { getLayoutShapeId } from "../../utils/floorPlanLayout/getLayoutShapeId";
import { layoutHighlightStore } from "../../utils/floorPlanLayout/layoutHighlightStore";
import type { RoomListProps } from "./types";

export const RoomList = ({
  rooms,
  areRoomsShown,
  onToggleShown,
  isOpen,
  onOpenChange,
}: RoomListProps) => {
  const { t } = useTranslation();
  const disclosure = useSectionDisclosure({ isOpen, onOpenChange });
  const sorted = [...rooms].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  const highlight = (roomId: number | null) =>
    layoutHighlightStore.set(roomId === null ? null : getLayoutShapeId({ kind: "room", id: roomId }));

  // A row unmounted under the cursor never says it was left.
  useEffect(() => () => layoutHighlightStore.set(null), []);

  return (
    <PanelSection
      testId="plan-rooms"
      tone="room"
      title={t("smartSurveyor.cablesBeta.roomsTitle")}
      count={rooms.length}
      isOpen={disclosure.isOpen}
      onToggle={disclosure.toggle}
      action={
        <button
          type="button"
          aria-pressed={areRoomsShown}
          onClick={() => onToggleShown(!areRoomsShown)}
          title={t(areRoomsShown ? "smartSurveyor.cablesBeta.roomsHide" : "smartSurveyor.cablesBeta.roomsShow")}
          className={clsx(
            "mr-1.5 grid h-9 w-9 flex-none place-items-center rounded-md border",
            areRoomsShown
              ? "border-slate-300 bg-white text-slate-700"
              : "border-transparent text-slate-400 hover:border-slate-300 hover:bg-white",
          )}
        >
          {areRoomsShown ? <Eye size={17} /> : <EyeOff size={17} />}
        </button>
      }
    >
      <p className="text-[13px] leading-snug text-slate-600">{t("smartSurveyor.cablesBeta.roomsWhat")}</p>
      {sorted.length > 0 && (
        <ul className="mt-2 space-y-0.5" onMouseLeave={() => highlight(null)}>
          {sorted.map((room) => (
            <li
              key={room.id}
              data-testid={`plan-room-${room.id}`}
              onMouseEnter={() => highlight(room.id)}
              className="flex items-center gap-2 rounded px-1 py-1 text-[13px] text-slate-800 hover:bg-slate-50"
            >
              <span
                aria-hidden
                className="h-3 w-3 flex-none rounded-[3px] border border-black/10"
                style={{ backgroundColor: room.color }}
              />
              <span className="min-w-0 flex-1 truncate">{room.name}</span>
            </li>
          ))}
        </ul>
      )}
    </PanelSection>
  );
};
